import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import ImageUpload from './ImageUpload';

const AdminAdmissionsEdit = () => {
  const [formData, setFormData] = useState({
    // Banner Section
    bannerTitle: '',
    bannerSubtitle: '',
    bannerImage: '',

    // B.Tech Section
    btechEligibilityCriteria: [],
    btechApplicationProcess: [],
    btechFeeStructureNote: '',

    // Polytechnic Section
    polytechnicEligibilityCriteria: [],
    polytechnicApplicationProcess: [],
    polytechnicLateralEntryNote: '',

    // Payment Section
    paymentOptions: [],
    paymentGuidelines: [],
    paymentImportantNotes: [],

    // Bank Details
    bankAccountName: '',
    bankName: '',
    bankAccountNumber: '',
    bankIFSCCode: '',
    bankBranch: '',
    bankImportantNote: '',
    upiQrCode: '',

    // Documents Section
    requiredDocuments: [],
    documentGuidelines: [],

    // Helplines
    admissionHelpline: '',
    admissionHelplineHours: '',
    emailSupport: '',
    emailSupportNote: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('banner');

  useEffect(() => {
    fetchAdmissionsData();
  }, []);

  const fetchAdmissionsData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAdmissionsAdmin();
      if (response.success) {
        setFormData(response.data);
      }
    } catch (error) {
      console.error('Error fetching admissions data:', error);
      setMessage('Error loading admissions data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setMessage('');
      
      const response = await apiService.updateAdmissions(formData);
      
      if (response.success) {
        setMessage('Admissions page updated successfully!');
        // Update form data with returned data (includes timestamps, etc.)
        setFormData(response.data);
      }
    } catch (error) {
      console.error('Error updating admissions data:', error);
      setMessage('Error updating admissions page');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const renderArrayEditor = (label, field, placeholder = '') => (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      <div className="space-y-2">
        {formData[field].map((item, index) => (
          <div key={index} className="flex gap-2">
            <input
              type="text"
              value={item}
              onChange={(e) => handleArrayChange(field, index, e.target.value)}
              placeholder={placeholder}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
            />
            <button
              type="button"
              onClick={() => removeArrayItem(field, index)}
              className="px-3 py-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition-colors"
            >
              <i className="fas fa-trash text-xs"></i>
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => addArrayItem(field)}
          className="px-4 py-2 bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200 transition-colors text-sm"
        >
          <i className="fas fa-plus mr-1"></i> Add Item
        </button>
      </div>
    </div>
  );

  const tabs = [
    { id: 'banner', label: 'Banner & General', icon: 'fas fa-image' },
    { id: 'btech', label: 'B.Tech Process', icon: 'fas fa-graduation-cap' },
    { id: 'polytechnic', label: 'Polytechnic Process', icon: 'fas fa-tools' },
    { id: 'payment', label: 'Payment & Bank Details', icon: 'fas fa-credit-card' },
    { id: 'documents', label: 'Documents & Support', icon: 'fas fa-file-alt' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">
          <i className="fas fa-spinner fa-spin mr-2"></i>
          Loading admissions data...
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-sans font-bold text-slate">Edit Admissions Page</h1>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="px-6 py-2 bg-crimson text-white rounded-lg font-medium hover:bg-crimson/90 disabled:opacity-50 transition-colors"
        >
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save mr-2"></i>
              Save Changes
            </>
          )}
        </button>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          message.includes('Error') 
            ? 'bg-red-50 border border-red-200 text-red-700' 
            : 'bg-green-50 border border-green-200 text-green-700'
        }`}>
          {message}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'border-crimson text-crimson'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <i className={`${tab.icon} mr-2`}></i>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="p-6">
          {/* Banner Tab */}
          {activeTab === 'banner' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Banner Configuration</h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Banner Title</label>
                <input
                  type="text"
                  value={formData.bannerTitle}
                  onChange={(e) => handleInputChange('bannerTitle', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Banner Subtitle</label>
                <input
                  type="text"
                  value={formData.bannerSubtitle}
                  onChange={(e) => handleInputChange('bannerSubtitle', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Banner Image</label>
                <ImageUpload
                  onChange={(imageUrl) => handleInputChange('bannerImage', imageUrl)}
                  value={formData.bannerImage}
                  placeholder="Banner image filename"
                />
              </div>
            </div>
          )}

          {/* B.Tech Tab */}
          {activeTab === 'btech' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">B.Tech Admission Process</h2>
              
              {renderArrayEditor('Eligibility Criteria', 'btechEligibilityCriteria', 'Enter eligibility criterion')}
              {renderArrayEditor('Application Process Steps', 'btechApplicationProcess', 'Enter process step')}
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Lateral Entry</label>
                <textarea
                  value={formData.btechFeeStructureNote}
                  onChange={(e) => handleInputChange('btechFeeStructureNote', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
                  placeholder="Enter lateral entry information for B.Tech programs"
                />
              </div>
            </div>
          )}

          {/* Polytechnic Tab */}
          {activeTab === 'polytechnic' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Polytechnic Admission Process</h2>
              
              {renderArrayEditor('Eligibility Criteria', 'polytechnicEligibilityCriteria', 'Enter eligibility criterion')}
              {renderArrayEditor('Application Process Steps', 'polytechnicApplicationProcess', 'Enter process step')}
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Lateral Entry Note</label>
                <textarea
                  value={formData.polytechnicLateralEntryNote}
                  onChange={(e) => handleInputChange('polytechnicLateralEntryNote', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
                />
              </div>
            </div>
          )}

          {/* Payment Tab */}
          {activeTab === 'payment' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Payment & Bank Details</h2>
              
              {renderArrayEditor('Payment Options', 'paymentOptions', 'Enter payment option')}
              {renderArrayEditor('Payment Guidelines', 'paymentGuidelines', 'Enter guideline')}
              {renderArrayEditor('Important Notes', 'paymentImportantNotes', 'Enter important note')}
              
              <hr className="my-8" />
              
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Bank Details</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Account Name</label>
                  <input
                    type="text"
                    value={formData.bankAccountName}
                    onChange={(e) => handleInputChange('bankAccountName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
                  <input
                    type="text"
                    value={formData.bankName}
                    onChange={(e) => handleInputChange('bankName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
                  <input
                    type="text"
                    value={formData.bankAccountNumber}
                    onChange={(e) => handleInputChange('bankAccountNumber', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">IFSC Code</label>
                  <input
                    type="text"
                    value={formData.bankIFSCCode}
                    onChange={(e) => handleInputChange('bankIFSCCode', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Branch Name</label>
                  <input
                    type="text"
                    value={formData.bankBranch}
                    onChange={(e) => handleInputChange('bankBranch', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Bank Important Note</label>
                <textarea
                  value={formData.bankImportantNote}
                  onChange={(e) => handleInputChange('bankImportantNote', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">UPI QR Code</label>
                <p className="text-xs text-gray-500 mb-2">Upload UPI payment QR code image for student payments</p>
                <ImageUpload
                  onChange={(imageUrl) => handleInputChange('upiQrCode', imageUrl)}
                  value={formData.upiQrCode}
                  placeholder="UPI QR Code image"
                />
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4">Documents & Support</h2>
              
              {renderArrayEditor('Required Documents', 'requiredDocuments', 'Enter document name')}
              {renderArrayEditor('Document Guidelines', 'documentGuidelines', 'Enter guideline')}
              
              <hr className="my-8" />
              
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Support & Helplines</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Admission Helpline</label>
                  <input
                    type="text"
                    value={formData.admissionHelpline}
                    onChange={(e) => handleInputChange('admissionHelpline', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Helpline Hours</label>
                  <input
                    type="text"
                    value={formData.admissionHelplineHours}
                    onChange={(e) => handleInputChange('admissionHelplineHours', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Support</label>
                  <input
                    type="email"
                    value={formData.emailSupport}
                    onChange={(e) => handleInputChange('emailSupport', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Support Note</label>
                  <input
                    type="text"
                    value={formData.emailSupportNote}
                    onChange={(e) => handleInputChange('emailSupportNote', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-4 rounded-b-lg">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {formData.lastModified && (
                <>
                  Last modified: {new Date(formData.lastModified).toLocaleDateString()} 
                  {formData.modifier?.username && ` by ${formData.modifier.username}`}
                </>
              )}
            </div>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-crimson text-white rounded-lg font-medium hover:bg-crimson/90 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminAdmissionsEdit;